import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { differenceInDays, addDays, compareAsc } from 'date-fns';
import { 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { 
    countInflightSales, 
    countAndAverageInvoicesByField,
    countInvoicesByFieldStackedBarChart,
    countAndTotalInvoicesByFieldAndDuration,
} from '../utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import ChartDialog from 'components/chartNivo/ChartDialog';
import PieChart from 'components/chartNivo/PieChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "revenue", label: "Revenue" },
  { value: "totalAmountByDate", label: "Total by Date" },
  { value: "directAmountByDate", label: "Direct by Date" },
  { value: "preorderAmountByDate", label: "Preorder by Date" },
];

const SalesChart = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);

    const { startDate, endDate } = useDateRangeContext();
    const dateDiff = differenceInDays(endDate, startDate) + 1;

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);

    const [view, setView] = useState('revenue');
    const [data, setData] = useState({
        revenuePie: [],
        revenueBar: [],

        stackedBarTotalAmountByDate: [],
        totalAmountByDate: [],
        totalAmount1InvoiceByDate: [],

        directAmountByDate: [],
        directAmount1InvoiceByDate: [],

        preorderAmountByDate: [],
        preorderAmount1InvoiceByDate: [],
    }); 

    useEffect(() => {
        const handleInvoices = async () => {
            if (!startDate || !endDate) return null; 

            try {           
                setError(null);
                setLoading(true);
                
                const url = '/inflightsales/invoices';
                const payload = { 
                    startDate: addDays(startDate, -1), 
                    endDate: addDays(endDate, 1),
                };

                const res = await api.post(url, payload); 
                  
                let result = [];  
                if (res.data) {        
                    const filteredInvoices = res.data.filter(invoice => {
                        const flightDateToCompare = 
                            timeMode === 'UTC' 
                                ? invoice.flightDate 
                                : invoice.flightDateLocalBase;
                        
                        return (
                            compareAsc(flightDateToCompare, startDate) >= 0 &&
                            compareAsc(flightDateToCompare, endDate) <= 0
                        );                    
                    })
                    result = filteredInvoices;
                };           
                
                const figures = {
                    revenuePie: countInflightSales(result, 'pie'), 
                    revenueBar: countInflightSales(result, 'bar'), 
                    
                    stackedBarTotalAmountByDate: countInvoicesByFieldStackedBarChart(result, timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'),
                    totalAmountByDate: countAndTotalInvoicesByFieldAndDuration(result, 'totalAmount', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase', dateDiff), 
                    totalAmount1InvoiceByDate: countAndAverageInvoicesByField(result, 'totalAmount', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'), 
        
                    directAmountByDate: countAndTotalInvoicesByFieldAndDuration(result, 'directAmount', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase', dateDiff), 
                    directAmount1InvoiceByDate: countAndAverageInvoicesByField(result, 'directAmount', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'), 
        
                    preorderAmountByDate: countAndTotalInvoicesByFieldAndDuration(result, 'preorderAmount', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase', dateDiff), 
                    preorderAmount1InvoiceByDate: countAndAverageInvoicesByField(result, 'preorderAmount', timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'), 
                };            
                setData(figures);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        }

        handleInvoices();        
    }, [timeMode, startDate, endDate, dateDiff]);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Onboard Sales ${period}`;

    const chartMap = {
        revenue: [
            { component: <PieChart data={data.revenuePie} title={defaultTitle} />, style: {} },
            { component: <SortBarChart data={data.revenueBar} title={defaultTitle} bottomLegend="Revenue" leftLegend="Revenue (millions VND)" isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
        ],
        totalAmountByDate: [
            { component: <SortStackedBarChart data={data.stackedBarTotalAmountByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue (millions VND)" isEnableTotals={false} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.totalAmountByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue (millions VND)" barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.totalAmount1InvoiceByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue/ Invoice (millions VND)" barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
        directAmountByDate: [
            { component: <SortBarMultiLinesChart data={data.directAmountByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue (millions VND)" barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.directAmount1InvoiceByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue/ Invoice (millions VND)" barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
        preorderAmountByDate: [
            { component: <SortBarMultiLinesChart data={data.preorderAmountByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue (millions VND)" barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortBarMultiLinesChart data={data.preorderAmount1InvoiceByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Revenue/ Invoice (millions VND)" barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
        ],
    };

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Sales" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <FormControl
                        size="small"
                        sx={{ width: { xs: '8rem', sm: '9rem' } }}
                    >
                        <InputLabel id="revenue-view-label">View</InputLabel>
                        <Select
                            labelId="revenue-view-label"
                            label="View"
                            value={view}
                            onChange={(e) => setView(e.target.value)}
                        >
                            {viewOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}  
            </FlexBetween>
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error.message}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    {view in chartMap &&
                        renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}    
        </Box>
    );
};

export default SalesChart;
